import { capabilities, products, projects } from "./data/site";
import { teamMembers } from "./data/team";
import { type Locale, translateContent } from "./i18n";

export const SITE_URL = "https://accsci.top";
export const SITE_NAME = "加速科学 AccSci";
export const DEFAULT_SOCIAL_IMAGE = "/images/team/team-office-background.webp";

export const INDEXABLE_PATHS = [
  "/",
  "/capabilities",
  "/work",
  ...projects.map((project) => `/work/${project.slug}`),
  "/products",
  "/about",
  "/contact",
  "/logo",
] as const;

export type PageSeo = {
  title: string;
  description: string;
  canonical: string;
  alternates: { zh: string; en: string; default: string };
  robots: string;
  image: string;
  imageWidth: number;
  imageHeight: number;
  locale: Locale;
  type: "website" | "article";
  jsonLd: Record<string, unknown>;
};

type LocalizedText = { zh: string; en: string };

const routeMetadata: Record<
  string,
  { title: LocalizedText; description: LocalizedText }
> = {
  "/": {
    title: {
      zh: "加速科学 AccSci — AI 原生产品工程",
      en: "AccSci — AI-native Product Engineering",
    },
    description: {
      zh: "加速科学（AccSci）提供 AI 软件、SaaS、Agent、Edge AI、IoT 与智能硬件的一体化产品工程服务，从快速原型走向规模化交付。",
      en: "AccSci builds AI software, SaaS, agents, Edge AI, IoT, and smart hardware, taking products from rapid prototype to production at scale.",
    },
  },
  "/capabilities": {
    title: {
      zh: "AI 软件与智能硬件服务",
      en: "AI Software and Smart Hardware Services",
    },
    description: {
      zh: "AccSci 的产品工程能力覆盖 LLM、AI Agent、RAG、模型部署、云端系统、Edge AI、IoT、PCB、嵌入式固件、样机与量产支持。",
      en: "AccSci delivers LLM, AI agent, RAG, model deployment, cloud, Edge AI, IoT, PCB, embedded firmware, prototyping, and production support.",
    },
  },
  "/work": {
    title: { zh: "客户案例", en: "Client Work" },
    description: {
      zh: "查看 AccSci 已交付的 IoT 智能硬件、工业 B2B 网站与数字产品案例，以及从问题定义到真实上线的工程过程。",
      en: "Explore AccSci client work across IoT hardware, industrial B2B websites, and digital products, from problem definition to live delivery.",
    },
  },
  "/products": {
    title: { zh: "AI 与边缘智能产品", en: "AI and Edge Intelligence Products" },
    description: {
      zh: "了解 AccSci Orchestrator、Edge Hub 与 Device Kit 产品概念，覆盖 Agent 运行底座、边缘计算网关和智能设备开发。",
      en: "Explore AccSci Orchestrator, Edge Hub, and Device Kit concepts for agent runtimes, edge gateways, and smart-device development.",
    },
  },
  "/about": {
    title: { zh: "关于加速科学", en: "About AccSci" },
    description: {
      zh: "AccSci 是一支跨 AI、云原生、嵌入式、IoT、电子与结构设计的产品工程团队，面向真实场景交付可运行、可维护、可量产的系统。",
      en: "AccSci is a multidisciplinary product engineering team spanning AI, cloud, embedded systems, IoT, electronics, and mechanical design.",
    },
  },
  "/contact": {
    title: { zh: "提交项目想法", en: "Start a Project" },
    description: {
      zh: "向 AccSci 提交 AI 软件、SaaS、智能硬件、Edge AI 或 IoT 项目想法。无需完整需求文档，我们会一起确定最快的验证路径。",
      en: "Share an AI software, SaaS, smart hardware, Edge AI, or IoT idea with AccSci. A finished specification is not required.",
    },
  },
  "/logo": {
    title: { zh: "AccSci 品牌标志", en: "AccSci Brand Mark" },
    description: {
      zh: "AccSci 品牌标志、组合规范与适用场景，包括网站、设备铭牌、PCB 丝印、外壳和技术文档。",
      en: "The AccSci brand mark, lockups, and usage guidance for websites, device plates, PCB silkscreen, enclosures, and technical documents.",
    },
  },
};

const defaultRobots =
  "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1";
const noIndexRobots = "noindex,nofollow,noarchive";

function normalizePath(pathname: string) {
  if (!pathname || pathname === "/") return "/";
  return `/${pathname.replace(/^\/+|\/+$/g, "")}`;
}

export function localizedUrl(pathname: string, locale: Locale) {
  const path = normalizePath(pathname);
  return `${SITE_URL}${path}${locale === "en" ? "?lang=en" : ""}`;
}

function absoluteAssetUrl(path: string) {
  return new URL(path, SITE_URL).toString();
}

function pageTitle(base: string, locale: Locale, home = false) {
  if (home) return base;
  return `${base} — ${locale === "en" ? "AccSci" : SITE_NAME}`;
}

function breadcrumbName(path: string, locale: Locale) {
  const metadata = routeMetadata[path];
  if (metadata) return metadata.title[locale];
  const project = projects.find((item) => `/work/${item.slug}` === path);
  if (project) return translateContent(project.title, locale);
  return locale === "en" ? "Page" : "页面";
}

function breadcrumbGraph(pathname: string, locale: Locale) {
  if (pathname === "/") return null;
  const parts = pathname.split("/").filter(Boolean);
  const paths = parts.map(
    (_, index) => `/${parts.slice(0, index + 1).join("/")}`,
  );
  return {
    "@type": "BreadcrumbList",
    "@id": `${localizedUrl(pathname, locale)}#breadcrumb`,
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: locale === "en" ? "Home" : "首页",
        item: localizedUrl("/", locale),
      },
      ...paths.map((path, index) => ({
        "@type": "ListItem",
        position: index + 2,
        name: breadcrumbName(path, locale),
        item: localizedUrl(path, locale),
      })),
    ],
  };
}

function organizationGraph(locale: Locale) {
  return {
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: locale === "en" ? "AccSci" : SITE_NAME,
    alternateName: ["AccSci", "加速科学"],
    url: `${SITE_URL}/`,
    logo: {
      "@type": "ImageObject",
      url: absoluteAssetUrl("/brand/accsci-logo.svg"),
    },
    email: "hello@accsci.top",
    description: routeMetadata["/"].description[locale],
    knowsAbout: [
      "AI-native applications",
      "AI agents",
      "Large language models",
      "Edge AI",
      "Internet of Things",
      "Embedded systems",
      "Smart hardware",
      "Product engineering",
    ],
  };
}

function websiteGraph(locale: Locale) {
  return {
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: `${SITE_URL}/`,
    name: locale === "en" ? "AccSci" : SITE_NAME,
    inLanguage: locale === "en" ? "en" : "zh-CN",
    publisher: { "@id": `${SITE_URL}/#organization` },
  };
}

function pageGraph(
  pathname: string,
  locale: Locale,
  title: string,
  description: string,
  pageType = "WebPage",
) {
  const canonical = localizedUrl(pathname, locale);
  return {
    "@type": pageType,
    "@id": `${canonical}#webpage`,
    url: canonical,
    name: title,
    description,
    inLanguage: locale === "en" ? "en" : "zh-CN",
    isPartOf: { "@id": `${SITE_URL}/#website` },
    about: { "@id": `${SITE_URL}/#organization` },
    breadcrumb:
      pathname === "/" ? undefined : { "@id": `${canonical}#breadcrumb` },
  };
}

function routeSpecificGraph(pathname: string, locale: Locale) {
  if (pathname === "/capabilities") {
    const localized = translateContent(capabilities, locale);
    return {
      "@type": "ItemList",
      "@id": `${localizedUrl(pathname, locale)}#services`,
      name: locale === "en" ? "AccSci services" : "AccSci 服务能力",
      numberOfItems: localized.length,
      itemListElement: localized.map((capability, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "Service",
          "@id": `${localizedUrl(pathname, locale)}#${capability.id}`,
          name: capability.title,
          description: capability.description,
          serviceType: capability.group,
          provider: { "@id": `${SITE_URL}/#organization` },
        },
      })),
    };
  }

  if (pathname === "/work") {
    const localized = translateContent(projects, locale);
    return {
      "@type": "ItemList",
      "@id": `${localizedUrl(pathname, locale)}#work`,
      name: locale === "en" ? "AccSci work" : "AccSci 项目案例",
      numberOfItems: localized.length,
      itemListElement: localized.map((project, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "CreativeWork",
          "@id": `${localizedUrl(`/work/${project.slug}`, locale)}#case-study`,
          url: localizedUrl(`/work/${project.slug}`, locale),
          name: project.title,
          abstract: project.summary,
          image: absoluteAssetUrl(project.image.src),
          creator: { "@id": `${SITE_URL}/#organization` },
          keywords: project.tags,
        },
      })),
    };
  }

  if (pathname === "/products") {
    const localized = translateContent(products, locale);
    return {
      "@type": "ItemList",
      "@id": `${localizedUrl(pathname, locale)}#products`,
      name: locale === "en" ? "AccSci product concepts" : "AccSci 产品概念",
      numberOfItems: localized.length,
      itemListElement: localized.map((product, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "Product",
          "@id": `${localizedUrl(pathname, locale)}#${product.id}`,
          name: product.name,
          description: product.description,
          category: product.tagline,
          image: absoluteAssetUrl(product.image.src),
          brand: { "@id": `${SITE_URL}/#organization` },
        },
      })),
    };
  }

  if (pathname === "/about") {
    const localized = translateContent(teamMembers, locale);
    return {
      "@type": "ItemList",
      "@id": `${localizedUrl(pathname, locale)}#team`,
      name: locale === "en" ? "AccSci team" : "AccSci 团队",
      numberOfItems: localized.length,
      itemListElement: localized.map((member, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "Person",
          name: member.name,
          jobTitle: member.role,
          description: member.biography,
          worksFor: { "@id": `${SITE_URL}/#organization` },
        },
      })),
    };
  }

  if (pathname === "/contact") {
    return {
      "@type": "ContactPoint",
      "@id": `${SITE_URL}/#contact`,
      contactType: "project inquiries",
      email: "hello@accsci.top",
      availableLanguage: ["Chinese", "English"],
    };
  }

  if (pathname === "/logo") {
    return {
      "@type": "ImageObject",
      "@id": `${localizedUrl(pathname, locale)}#brand-image`,
      name: locale === "en" ? "AccSci brand mark" : "AccSci 品牌标志",
      contentUrl: absoluteAssetUrl("/brand/accsci-logo.svg"),
      creator: { "@id": `${SITE_URL}/#organization` },
    };
  }

  const project = projects.find((item) => `/work/${item.slug}` === pathname);
  if (project) {
    const localized = translateContent(project, locale);
    return {
      "@type": "CreativeWork",
      "@id": `${localizedUrl(pathname, locale)}#case-study`,
      url: localizedUrl(pathname, locale),
      name: localized.title,
      headline: localized.systemTitle,
      abstract: localized.summary,
      description: localized.resultNote,
      image: absoluteAssetUrl(localized.image.src),
      creator: { "@id": `${SITE_URL}/#organization` },
      about: localized.industry,
      keywords: localized.tags,
      inLanguage: locale === "en" ? "en" : "zh-CN",
    };
  }

  return null;
}

function structuredData(
  pathname: string,
  locale: Locale,
  title: string,
  description: string,
) {
  const pageType =
    pathname === "/about"
      ? "AboutPage"
      : pathname === "/contact"
        ? "ContactPage"
        : pathname === "/work" || pathname === "/products"
          ? "CollectionPage"
          : "WebPage";
  const graph: Record<string, unknown>[] = [
    organizationGraph(locale),
    websiteGraph(locale),
    pageGraph(pathname, locale, title, description, pageType),
  ];
  const breadcrumb = breadcrumbGraph(pathname, locale);
  const specific = routeSpecificGraph(pathname, locale);
  if (breadcrumb) graph.push(breadcrumb);
  if (specific) graph.push(specific);
  return { "@context": "https://schema.org", "@graph": graph };
}

export function getPageSeo(
  pathname: string,
  locale: Locale,
  fallbackTitle?: string,
): PageSeo {
  const path = normalizePath(pathname);
  const project = projects.find((item) => `/work/${item.slug}` === path);
  const localizedProject = project ? translateContent(project, locale) : null;
  const metadata = routeMetadata[path];
  const indexable = Boolean(metadata || localizedProject);
  const baseTitle =
    metadata?.title[locale] ??
    localizedProject?.title ??
    fallbackTitle ??
    (locale === "en" ? "Page not found" : "页面未找到");
  const description =
    metadata?.description[locale] ??
    localizedProject?.summary ??
    (locale === "en"
      ? "This page is not available on AccSci."
      : "AccSci 上没有找到这个页面。");
  const title = pageTitle(baseTitle, locale, path === "/");
  const image = localizedProject?.image.src ?? DEFAULT_SOCIAL_IMAGE;
  const imageWidth = localizedProject?.image.width ?? 1672;
  const imageHeight = localizedProject?.image.height ?? 941;
  const canonical = localizedUrl(path, locale);

  return {
    title,
    description,
    canonical,
    alternates: {
      zh: localizedUrl(path, "zh"),
      en: localizedUrl(path, "en"),
      default: localizedUrl(path, "zh"),
    },
    robots: indexable ? defaultRobots : noIndexRobots,
    image: absoluteAssetUrl(image),
    imageWidth,
    imageHeight,
    locale,
    type: localizedProject ? "article" : "website",
    jsonLd: indexable
      ? structuredData(path, locale, title, description)
      : { "@context": "https://schema.org", "@type": "WebPage", name: title },
  };
}

function link(path: string, locale: Locale) {
  return localizedUrl(path, locale);
}

export function renderLlmsTxt() {
  return `# AccSci / 加速科学

> AccSci is an AI-native product engineering team building AI software, SaaS, agents, Edge AI, IoT systems, and smart hardware from prototype to production.

AccSci（加速科学）是一支 AI 原生产品工程团队，面向真实业务和设备场景交付 AI 软件、SaaS、Agent、Edge AI、IoT 与智能硬件。

## Core pages

- [Home](${link("/", "en")}): Company overview and delivery model.
- [Services](${link("/capabilities", "en")}): AI, cloud, embedded, electronics, mechanical, prototype, and production capabilities.
- [Products](${link("/products", "en")}): AccSci Orchestrator, Edge Hub, and Device Kit concepts.
- [Client work](${link("/work", "en")}): Delivered project case studies.
- [About](${link("/about", "en")}): Team disciplines and engineering principles.
- [Start a project](${link("/contact", "en")}): Project inquiry details.

## Case studies

${projects
  .map(
    (project) =>
      `- [${project.title}](${link(`/work/${project.slug}`, "zh")}): ${project.summary}`,
  )
  .join("\n")}

## Machine-readable resources

- [Full bilingual company content](${SITE_URL}/llms-full.txt)
- [XML sitemap](${SITE_URL}/sitemap.xml)
- [Crawler policy](${SITE_URL}/robots.txt)

## Contact

- Email: hello@accsci.top
- Canonical domain: ${SITE_URL}
`;
}

function markdownSection(locale: Locale) {
  const localizedCapabilities = translateContent(capabilities, locale);
  const localizedProducts = translateContent(products, locale);
  const localizedProjects = translateContent(projects, locale);
  const localizedTeam = translateContent(teamMembers, locale);
  const language = locale === "en" ? "English" : "中文";
  return `# ${language}

## Organization

${routeMetadata["/"].description[locale]}

- Website: ${link("/", locale)}
- Contact: hello@accsci.top
- Focus: AI-native applications, agents, model engineering, cloud systems, Edge AI, IoT, embedded systems, electronics, smart hardware, prototyping, and production support.

## Services

${localizedCapabilities
  .map(
    (capability) =>
      `### ${capability.title}\n\n${capability.description}\n\nCategory: ${capability.group}`,
  )
  .join("\n\n")}

## Products

${localizedProducts
  .map(
    (product) =>
      `### ${product.name}\n\n${product.tagline} ${product.description}\n\nFeatures: ${product.features.join(", ")}`,
  )
  .join("\n\n")}

## Project case studies

${localizedProjects
  .map(
    (project) =>
      `### ${project.title}\n\nURL: ${link(`/work/${project.slug}`, locale)}\n\nClient: ${project.client}\n\nIndustry: ${project.industry}\n\nSummary: ${project.summary}\n\nChallenge: ${project.challenge}\n\nApproach: ${project.approach}\n\nResult: ${project.result}. ${project.resultNote}\n\nDelivery: ${project.delivery}`,
  )
  .join("\n\n")}

## Team

${localizedTeam
  .map(
    (member) =>
      `### ${member.name}\n\nRole: ${member.role}${member.credential ? `\n\nCredential: ${member.credential}` : ""}\n\n${member.biography}`,
  )
  .join("\n\n")}
`;
}

export function renderLlmsFull() {
  return `# AccSci / 加速科学 — Full Company Content

This file is a bilingual, machine-readable representation of the public content published on ${SITE_URL}. The canonical HTML pages remain the authoritative source.

${markdownSection("zh")}

---

${markdownSection("en")}
`;
}
